if (!customElements.get('the-fold-stories')) {
  customElements.define('the-fold-stories', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.querySelectorAll('[data-stories-clone]').forEach((slide) => slide.remove());
      this.viewport = this.querySelector('[data-stories-viewport]');
      this.track = this.querySelector('[data-stories-track]');
      this.slides = [...this.querySelectorAll('[data-stories-slide]')];
      this.dots = [...this.querySelectorAll('[data-stories-dot]')];
      this.pauseButton = this.querySelector('[data-stories-pause]');
      if (!this.viewport || !this.track || !this.slides.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.reducedMotion.matches;
      this.index = 0;
      this.position = this.slides.length;
      this.remaining = 3000;
      this.slides.forEach((slide, index) => { slide.dataset.storiesIndex = index; });
      for (const side of ['before', 'after']) {
        const fragment = document.createDocumentFragment();
        for (const slide of this.slides) {
          const clone = slide.cloneNode(true);
          clone.setAttribute('data-stories-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.inert = true;
          fragment.append(clone);
        }
        if (side === 'before') this.track.prepend(fragment);
        else this.track.append(fragment);
      }
      this.querySelectorAll('[data-stories-previous]').forEach((button) => button.addEventListener('click', () => this.goTo(this.index - 1), { signal }));
      this.querySelectorAll('[data-stories-next]').forEach((button) => button.addEventListener('click', () => this.goTo(this.index + 1), { signal }));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index), { signal }));
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.updatePlayback();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.reducedMotion.addEventListener('change', () => {
        this.paused = this.reducedMotion.matches;
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('keydown', (event) => {
        const directions = { ArrowLeft: -1, ArrowRight: 1 };
        if (event.key in directions) {
          event.preventDefault();
          this.goTo(this.index + directions[event.key]);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.goTo(event.key === 'Home' ? 0 : this.slides.length - 1);
        }
      }, { signal });
      this.viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        this.normalize();
        this.drag = { id: event.pointerId, start: event.clientX, delta: 0, time: performance.now() };
        this.viewport.setPointerCapture(event.pointerId);
        this.updatePlayback();
      }, { signal });
      this.viewport.addEventListener('pointermove', (event) => {
        if (this.drag?.id !== event.pointerId) return;
        this.drag.delta = event.clientX - this.drag.start;
        this.render(false, this.drag.delta);
      }, { signal });
      for (const type of ['pointerup', 'pointercancel']) {
        this.viewport.addEventListener(type, (event) => {
          if (this.drag?.id !== event.pointerId) return;
          const { delta, time } = this.drag;
          this.drag = null;
          if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
          const distance = Math.abs(delta) / this.stride;
          const steps = type === 'pointercancel' || Math.abs(delta) <= 5 ? 0 : Math.sign(-delta) * (performance.now() - time > 300 ? Math.round(distance) : Math.ceil(distance));
          this.goTo(this.index + steps);
        }, { signal });
      }
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.normalize();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-stories-slide]'));
        if (index < 0) return;
        this.paused = true;
        this.goTo(index);
      }, { signal });
      this.resizeObserver = new ResizeObserver(() => this.normalize());
      this.resizeObserver.observe(this.viewport);
      this.render(false);
      this.updatePlayback();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    updatePlayback() {
      if (this.pauseButton) this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
      const stopped = this.paused || this.hovered || this.focused || this.drag || document.hidden || this.slides.length < 2;
      if (this.timer && stopped) {
        this.remaining = Math.max(0, this.nextAt - performance.now());
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (stopped || this.timer) return;
      this.nextAt = performance.now() + this.remaining;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.goTo(this.index + 1);
      }, this.remaining);
    }

    normalize() {
      this.position = this.slides.length + this.index;
      this.render(false);
    }

    goTo(index) {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.position < this.slides.length || this.position >= this.slides.length * 2) {
        this.normalize();
        this.track.getBoundingClientRect();
      }
      const target = Math.max(-this.slides.length, Math.min(this.slides.length * 2 - 1, index));
      this.position = this.slides.length + target;
      this.index = ((target % this.slides.length) + this.slides.length) % this.slides.length;
      this.render(!this.reducedMotion.matches);
      if (this.reducedMotion.matches) this.normalize();
      this.remaining = this.reducedMotion.matches ? 3000 : 3300;
      this.updatePlayback();
    }

    render(animate, delta = 0) {
      const slide = this.slides[0];
      this.stride = slide.getBoundingClientRect().width + parseFloat(getComputedStyle(slide).marginRight);
      this.track.style.transition = animate ? 'transform 300ms ease' : 'none';
      this.track.style.transform = `translate3d(${-this.position * this.stride + delta}px, 0, 0)`;
      for (const item of this.track.children) item.toggleAttribute('data-current', Number(item.dataset.storiesIndex) === this.index);
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  });
}
